/**
 * Industry pages: the types, and every page (one file each in src/marketing/trades/<slug>.ts). A page is published by listing its path in
 * routes.json; src/marketing/live.ts keeps only those.
 */

export type Verify = 'payments-live' | 'scheduling-live' | 'confirm';

export interface Section {
  id: string;
  h2: string;
  /** The problem, in the owner's words. */
  problem: string;
  /** How Scopie handles it. Plain paragraphs; keep them as text in the HTML (not images). */
  body: string[];
  verify?: Verify[];
}

export interface Faq {
  q: string;
  /** Plain text, FAQPage-ready (no markup, no links inside the answer). */
  a: string;
  verify?: Verify[];
}

/**
 * A page is an ordered list of blocks, so each trade page can have its own shape (its own sections, in its own order). The renderer adds no words of
 * its own: every heading, label and sentence is in the block. A page without `blocks` is the older shape (answer, example quote, sections, FAQ), which
 * src/marketing/blocks.ts turns into blocks.
 */
export type Block =
  | { type: 'text'; id: string; h2: string; /** the problem, in the customer's words (set apart in italics) */ lead?: string; paragraphs: string[]; /** words already in a paragraph (once, exactly as written) that link to a page; the text itself is unchanged */ links?: { phrase: string; href: string }[] }
  | { type: 'sample-quote'; id: string; h2?: string; label: string; lines: { item: string; amount: string }[]; total: string; totalLabel?: string; note?: string }
  | { type: 'list'; id: string; h2: string; style: 'bullets' | 'steps' | 'checklist' | 'mistakes'; intro?: string; items: { title?: string; text: string }[] }
  | { type: 'faq'; id: string; h2: string; items: { q: string; a: string }[] }
  | { type: 'image'; id: string; src: string; alt: string; caption?: string; width?: number; height?: number }
  | { type: 'callout'; id: string; text: string }
  /** A printable template (a template page): a sheet of fill-in lines, shown on the page and generated as a PDF from this same data. [Brackets] are placeholders. */
  | { type: 'document'; id: string; h2: string; intro?: string; sections: { heading: string; lines: string[] }[]; pdfName: string };

export interface IndustryPage {
  slug: string;
  /** Where it sits on the hub, the nav and the footer (lower first; ties by name). */
  order?: number;
  /** An optional heading on the hub when there are many trades (for example "Outdoor", "Cleaning", "Home services"). */
  group?: string;
  /** The home page trade tile (src/pages/landing/home/photos.ts) that links to this page. */
  tile?: string;
  path: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  /** <= 60 characters */
  title: string;
  /** <= 155 characters */
  metaDescription: string;
  h1: string;
  /** 40-60 words, first text under the H1 */
  answerFirst: string;
  answerFirstVerify?: Verify[];
  exampleQuote: {
    label: string;
    lines: { item: string; amount: string }[];
    total: string;
    depositNote: string;
  };
  /** The page as ordered blocks (the page's own shape). Absent: built from the fields below. */
  blocks?: Block[];
  sections: Section[];
  faq: Faq[];
  internalLinks: { href: string; anchor: string; status: 'live' | 'planned' }[];
  cta: { heading: string; text: string; button: string; href: string; fine: string };
  claimsToVerify: string[];
}

/**
 * Every other trade is ONE file in src/marketing/trades/<slug>.ts (default export: an IndustryPage written claim-free). Dropping the file in is the
 * whole code change; publishing it is listing its path in routes.json and the sitemap, and from there the hub, the nav, the footer and the prerender
 * pick it up. See src/marketing/trades/README.md.
 */
export const tradePages: IndustryPage[] = Object.values(import.meta.glob<{ default: IndustryPage }>('./trades/*.ts', { eager: true })).map((m) => m.default);

const byOrder = (a: IndustryPage, b: IndustryPage) => (a.order ?? 100) - (b.order ?? 100) || a.h1.localeCompare(b.h1);

/** What the site can show (published or not: src/marketing/live.ts keeps only the published ones). */
export const industryPages: IndustryPage[] = [...tradePages].sort(byOrder);
